import { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';
import { ResourcesHubIcon } from '@rsos/assets/icons';
import { ConditionalDisplay } from '@rsos/base-components';
import { UnreadNotificationIcon } from '@rsos/components/capstone/UnreadNotificationIcon/UnreadNotificationIcon';
import { Box, Button } from '@rsos/components/capstone/base';
import useHasAccessToResourceRequests from '@rsos/hooks/useHasAccessToResourceRequests';
import {
  useHasUnreadTransportRequests,
  useIsResourcePanelOpen,
  toggleResourcePanel,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import styles from './ResourceRequestBtn.module.css';

interface ResourceRequestBtnProps {
  onTrack?: (params: { panelAction: 'open' | 'close' }) => void;
}

/**
 * Navigation button for opening the Resource Request Panel in emergency_data.
 * Mount only from RightHandButtonNav when fe-app-name is emergency_data.
 * Other portal bundles lack the resourceRequests and api reducers this component needs,
 * which causes hydration errors.
 */
export const ResourceRequestBtn: FunctionComponent<ResourceRequestBtnProps> = ({
  onTrack,
}) => {
  const hasAccessToResourceRequests = useHasAccessToResourceRequests();
  const dispatch = useDispatch();
  const hasUnreadTransportRequests = useHasUnreadTransportRequests();
  const isResourcePanelOpen = useIsResourcePanelOpen();

  const handleClick = () => {
    onTrack?.({ panelAction: isResourcePanelOpen ? 'close' : 'open' });
    dispatch(toggleResourcePanel());
  };

  return (
    <ConditionalDisplay
      dataName="resource-request-nav-button"
      conditions={[
        {
          showIf: hasAccessToResourceRequests,
          reason:
            'Agency needs the Air Ambulance or TowPro capability enabled and the user role needs the matching permission',
        },
      ]}
    >
      <Box horizontal onClick={handleClick}>
        <span className={styles.resourceRequestNavButtonContainer}>
          <Button
            btnType="tertiary"
            size="small"
            type="button"
            dataName="resource-request-nav-button"
            aria-label="Request Resources"
          >
            <ResourcesHubIcon width={16} height={16} color="secondaryText" />
            {hasUnreadTransportRequests && (
              <UnreadNotificationIcon
                aria-hidden="true"
                data-testid="resource-request-nav-icon"
              />
            )}
          </Button>
        </span>
      </Box>
    </ConditionalDisplay>
  );
};

export default ResourceRequestBtn;
